import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { DataTableConfig, DataTableModule } from '@qtek/libs/data-table';
import { LazyDialogLoader } from '@qtek/libs/dialog-loader';
import { PermissionsService, PermissionType } from '@qtek/libs/permissions';
import { ErrorSnackBarData, SnackBarService } from '@qtek/libs/snack-bar-core';
import {
  ItemStore,
  NewRelationsStore,
  SchemaEditorActions,
  selectTasksLoadingByMysid,
  TASK_FORM_MYSID,
  TASK_MYSID,
  TASK_MYSID_TOKEN,
  TasksActions,
  TasksFeature,
  UserStore,
} from '@qtek/libs/store';
import { TaskTableStatusComponent } from '@qtek/libs/tasks';
import {
  TranslationCoreModule,
  TranslationCoreService,
} from '@qtek/libs/translation-core';
import {
  Column,
  TableTask,
  Task,
  TaskParams,
  TaskPayload,
  UserRole,
} from '@qtek/shared/models';
import { isWorkflowUploadTaskFor } from '@qtek/shared/utils';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { TaskDisplayRow, TaskListRow, TaskListTask } from './task-list.types';

@Component({
  selector: 'qt-task-list',
  templateUrl: './task-list.component.html',
  styleUrls: ['./task-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AsyncPipe,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    TranslationCoreModule,
    DataTableModule,
    MatSlideToggleModule,
    TaskTableStatusComponent,
  ],
  providers: [
    { provide: TASK_MYSID_TOKEN, useValue: TASK_MYSID.PERSONAL_TASKS },
  ],
})
export class TaskListComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly newRelationsStore = inject(NewRelationsStore);
  private readonly userStore = inject(UserStore);
  private readonly itemStore = inject(ItemStore);
  private readonly dialogLoader = inject(LazyDialogLoader);
  private readonly snackBar = inject(SnackBarService);
  private readonly translate = inject(TranslationCoreService);
  private readonly mysid = inject(TASK_MYSID_TOKEN);
  private readonly permissionsService = inject(PermissionsService);

  hasTasksUpdate = toSignal(
    this.permissionsService.isAllowed$('tasks', PermissionType.Update),
    { initialValue: false }
  );

  @Output() expandStateChange = new EventEmitter<boolean>();
  @Output() taskClick = new EventEmitter<Task>();

  isTaskListExpanded = signal<boolean>(true);
  private readonly groupByDealSubject = new BehaviorSubject<boolean>(true);
  readonly groupByDeal$ = this.groupByDealSubject.asObservable();

  tasks$: Observable<TaskListTask[]> = this.store
    .select(TasksFeature.selectTasksByMysid(this.mysid))
    .pipe(
      map(
        tasks =>
          tasks.filter(task => task.statusType === 'TODO') as TaskListTask[]
      )
    );

  isLoading = toSignal(
    this.store.select(selectTasksLoadingByMysid(this.mysid))
  );

  isParticipant = computed(
    () => this.userStore.relationType() === UserRole.RESTRICTED
  );

  displayedTasks$ = combineLatest([this.tasks$, this.groupByDeal$]).pipe(
    map(([tasks, groupByDeal]) => this.mapTaskRows(tasks, groupByDeal))
  );

  rowStylesCallback = (row: TaskListRow) =>
    row.kind === 'group'
      ? {
          'background-color': 'var(--app-power-search-surface-background)',
          color: 'var(--app-main-text)',
          'font-weight': '600',
          'min-height': '52px',
          display: 'grid',
          'grid-template-columns': '1fr 0 0 0',
          'grid-column': '1 / -1',
        }
      : {};

  taskTableColumns: Column[] = [
    {
      id: 'title',
      label: 'GNR_TITLE',
      minWidth: 120,
      maxWidth: 300,
      hideOrder: 0,
    },
    {
      id: 'dueDate',
      label: 'GNR_EXECUTE_DATE',
      minWidth: 120,
      maxWidth: 180,
      hideOrder: 1,
    },
    {
      id: 'status',
      label: 'GNR_STATUS',
      minWidth: 90,
      maxWidth: 170,
      hideOrder: 2,
    },
  ];

  dataTableConfig: Partial<DataTableConfig> = {
    selectable: false,
    hideSelect: true,
    menuButIcon: false,
    draggable: false,
    droppable: false,
    multiple: false,
    customGridColumns: 'repeat(3, minmax(0, auto))',
  };

  ngOnInit(): void {
    this.subscribeFormData();
    this.subscribeToTasks();
  }

  ngOnDestroy(): void {
    this.store.dispatch(TasksActions.unsubscribeTasks({ mysid: this.mysid }));
    this.unsubscribeSchemas();
    this.unsubscribeFormData();
  }

  onTaskClick(task: Task): void {
    if (task.expired) {
      const data: ErrorSnackBarData = {
        type: 'ErrorMessage',
        message: 'ERR_TASK_EXPIRED',
      };
      this.snackBar.showSnackBar(data);
      return;
    }

    if (
      (this.isUploadFileTask(task) && task.taskParams?.['token']?.[0]) ||
      isWorkflowUploadTaskFor(task, this.userStore.user()?._id)
    ) {
      this.openFileUploaderDialog(task);
      return;
    }

    this.openTaskDialog(task);
  }

  openTaskDialog(task?: Task): void {
    this.dialogLoader
      .open('tasks-dialog', {
        mode: task ? 'edit' : 'create',
        ...(task ? { ownTask: true, task } : {}),
        taskId: task?._id || null,
        mysid: this.mysid,
      })
      .subscribe();
  }

  private subscribeToTasks(): void {
    const payload = {
      view: 'personal',
      cmd: 'query' as const,
      prms: {
        statusType: 'TODO',
      },
    };

    this.store.dispatch(
      TasksActions.subscribeTasks({ payload, mysid: this.mysid })
    );
  }

  private subscribeFormData(): void {
    this.newRelationsStore.connectPeople({
      mysid: TASK_FORM_MYSID.ALL_CONTACTS,
      view: 'personCards',
      q: [
        {
          k: 'mysid',
          op: 'EQ',
          v: ['task_view_people'],
        },
      ],
      params: {
        cmd: 'query',
      },
    });

    this.itemStore.connect({
      mysid: TASK_FORM_MYSID.ORIGINATIONS_DEALS,
      view: 'salesPipeline',
    });
  }

  private unsubscribeSchemas(): void {
    this.store.dispatch(
      SchemaEditorActions.disconnectSchemas({
        mysid: 'task-form-schemas',
      })
    );
  }

  private unsubscribeFormData(): void {
    this.itemStore.disconnect(TASK_FORM_MYSID.ORIGINATIONS_DEALS);
  }

  private isUploadFileTask(task: Task): boolean {
    return task.actionType?.toLowerCase() === 'upload_doc';
  }

  private openFileUploaderDialog(task: Task): void {
    this.dialogLoader
      .open('file-uploader-dialog', {
        task: task,
        token: task.taskParams?.['token']?.[0] || undefined,
        mysid: this.mysid,
      })
      .subscribe();
  }

  onGroupByDealToggle(value: boolean): void {
    this.groupByDealSubject.next(value);
  }

  onRowClick(row: TaskListRow): void {
    if (row.kind === 'task') {
      this.onTaskClick(row.task);
    }
  }

  onTaskStatusChange(
    event: { task: TableTask; status: string },
    row: TaskListRow
  ): void {
    if (row.kind !== 'task') {
      return;
    }

    const executionTime = event.task.executionTime ?? row.dueDate ?? null;

    const updatePayload: TaskPayload = {
      ownerType: event.task.ownerType,
      statusType: event.status.toUpperCase(),
      actionType: event.task.actionType,
      taskParams: event.task.taskParams,
      executionTime,
      user: event.task.user,
    };

    this.store.dispatch(
      TasksActions.updateTask({
        payload: updatePayload,
        mysid: this.mysid,
        id: event.task._id,
      })
    );
  }

  trackByRow(_index: number, row: TaskListRow): string {
    return row.id;
  }

  getStatusValue(row: TaskListRow): 'todo' | 'done' | 'pending' {
    if (row.kind !== 'task') {
      return 'todo';
    }
    const status = row.status;
    if (status === 'done' || status === 'pending') {
      return status;
    }
    return 'todo';
  }

  private mapTaskRows(
    tasks: TaskListTask[],
    groupByDeal: boolean
  ): TaskListRow[] {
    const normalized = tasks.map(task => this.toDisplayRow(task));
    if (!groupByDeal) {
      return normalized;
    }

    const noDealLabel = this.translate.instant('GNR_NO_DEAL');
    const grouped = new Map<string, TaskDisplayRow[]>();
    normalized.forEach(row => {
      const key = row.dealName || noDealLabel;
      const groupRows = grouped.get(key) ?? [];
      groupRows.push(row);
      grouped.set(key, groupRows);
    });

    const result: TaskListRow[] = [];
    Array.from(grouped.entries())
      .sort(([a], [b]) => {
        if (a === noDealLabel && b !== noDealLabel) {
          return -1;
        }
        if (a !== noDealLabel && b === noDealLabel) {
          return 1;
        }
        return a.localeCompare(b);
      })
      .forEach(([label, rows]) => {
        result.push({ kind: 'group', id: `group-${label}`, label });
        result.push(...rows);
      });

    return result;
  }

  private toDisplayRow(task: TaskListTask): TaskDisplayRow {
    const params: TaskParams = task.taskParams ?? ({} as TaskParams);
    const title =
      params['title']?.[0] ||
      params['dealName']?.[0] ||
      this.getItemInfoName(task) ||
      task.actionType ||
      '';
    const dealName =
      params['dealName']?.[0] || this.getItemInfoName(task) || '';
    const dueDate =
      task.executionTime ??
      params['dueDate']?.[0] ??
      params['deadline']?.[0] ??
      null;

    return {
      kind: 'task',
      id: task._id,
      title,
      dealName,
      dueDate,
      status: (task.statusType || 'TODO').toLowerCase(),
      task,
      expired: !!task.expired,
    };
  }

  private getItemInfoName(task: Task): string | undefined {
    return task.itemInfo?.name;
  }
}
